import useAuth from '@/hooks/useAuth'
import { useCartState } from '@/contexts/CartContext'
import Image from 'next/image'
import Link from 'next/link'
import * as fbq from '@/services/fbpixel'
import { useRouter } from 'next/router'
import React, { Dispatch, SetStateAction, useCallback, memo, lazy, Suspense, useState, useMemo } from 'react'
import Button from './Button'
import { toast } from 'react-hot-toast'
import CustomToast from './CustomToast'
import LogoImage from '../../public/images/logo.svg'
import SearchDropdown from './SearchDropdown'
import { event } from 'nextjs-google-analytics'
import { useLoginModal } from '@/contexts/LoginModalContext'
import { 
  WishlistIcon,
  UserIcon,
  CartIcon,
  LeftArrowIcon,
  GridIcon
} from './Icons/DesktopHeaderIcons'
import CurrencyDropdown from './Header/CurrencyDropdown'
import { useTranslation } from 'next-i18next'
import useUserPreferences from '@/hooks/useUserPreferences'

// Lazy load LanguageSwitcher if not immediately needed
const LazyLanguageSwitcher = lazy(() => import('./Header/languageSwitcher'));

type Props = {
  setIsLoginOpen: (arg0: boolean) => void
  setIsRegisterOpen: (arg0: boolean) => void
  setIsForgotOpen: (arg0: boolean) => void
  setLastOpened: Dispatch<SetStateAction<'login' | 'register'>>
  lastOpened: 'login' | 'register'
  featured_categories: any
  setProductType?: any
}

const currencies = [
  { code: 'ILS', symbol: '₪', name: 'Israeli Shekel', countryCode: 'IL', countryName: 'Israel' },
  { code: 'USD', symbol: '$', name: 'US Dollar', countryCode: 'US', countryName: 'United States' },
  { code: 'EUR', symbol: '€', name: 'Euro', countryCode: 'EU', countryName: 'European Union' },
  { code: 'GBP', symbol: '£', name: 'British Pound', countryCode: 'GB', countryName: 'United Kingdom' },
  { code: 'PKR', symbol: '₨', name: 'Pakistani Rupee', countryCode: 'PK', countryName: 'Pakistan' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', countryCode: 'IN', countryName: 'India' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', countryCode: 'JP', countryName: 'Japan' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', countryCode: 'CN', countryName: 'China' },
  { code: 'CAD', symbol: '$', name: 'Canadian Dollar', countryCode: 'CA', countryName: 'Canada' },
  { code: 'AUD', symbol: '$', name: 'Australian Dollar', countryCode: 'AU', countryName: 'Australia' },
];

export const useCurrency = (currencyCode?: string) => {
  const [currency, setCurrency] = React.useState(() => {
    if (!currencyCode) return undefined;
    return currencies.find(curr => curr.code === currencyCode);
  });

  React.useEffect(() => {
    if (currencyCode) {
      const found = currencies.find(curr => curr.code === currencyCode);
      setCurrency(found);
    } else {
      setCurrency(undefined);
    }
  }, [currencyCode]);

  return {
    symbol: currency?.symbol ?? '',
    name: currency?.name ?? '',
    countryCode: currency?.countryCode,
    countryName: currency?.countryName,
  };
}

// Memoize category list item
const CategoryItem = memo(({ category, locale }: { category: any; locale: string }) => (
  <li>
    <Link href={`/products?categories=${encodeURIComponent(category.slug)}&page=1`}>
      {locale === 'ar' ? category.name?.ar : category.name?.en}
    </Link>
  </li>
));

CategoryItem.displayName = 'CategoryItem';

const DesktopHeader = memo(({
  setIsLoginOpen,
  setIsRegisterOpen,
  setIsForgotOpen,
  featured_categories,
  setLastOpened,
  lastOpened,
  setProductType
}: Props) => {
  const { t: tHeader } = useTranslation('desktopHeader');
  const { t: tAlert } = useTranslation('alert');
  const { locale } = useRouter();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const { setPostLoginCallback, setLocation } = useLoginModal();
  const cart = useCartState();
  const preferences = useUserPreferences();
  const router = useRouter();
  const { isLoading, user } = useAuth('none');
  const preferenceCurrency = preferences?.currencyCode;

  // Memoize currency code calculation
  const currencyCode = useMemo(() => {
    if (preferenceCurrency) return preferenceCurrency;
    if (cart?.currency) return cart.currency;
    return 'ILS';
  }, [preferenceCurrency, cart?.currency]);

  const currencyKey = useMemo(() => `${currencyCode}-${Date.now()}`, [currencyCode]);
  const { symbol } = useCurrency(currencyCode);

  // Memoize submit search
  const SubmitSearch = useCallback(() => {
    const params = new URLSearchParams({
      query: search,
      categories: category,
      page: '1'
    });
    event('search', { search_term: search });
    fbq.event('Search', { search_string: search });
    router.push('/products?' + params.toString());
  }, [search, category, router]);

  // Memoize wishlist click handler
  const handleWishlistClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (!user) {
      toast.custom((tEl) => <CustomToast t={tEl} message={tAlert('login_wishlist')} type='error' />, {
        duration: 2500,
      });
      setPostLoginCallback(() => router.push('/account/wishlist'));
      setProductType(null);
      setLocation('header');
      setIsLoginOpen(true);
      return;
    }
    router.push('/account/wishlist');
  }, [user, router, setPostLoginCallback, setProductType, setLocation, setIsLoginOpen, tAlert]);

  // Memoize user click handler
  const handleUserClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (user) {
      router.push('/account');
    } else {
      setPostLoginCallback(() => router.push('/account'));
      setProductType(null);
      setLocation('header');
      setIsLoginOpen(true);
    }
  }, [user, router, setPostLoginCallback, setProductType, setLocation, setIsLoginOpen]);

  // Memoize cart click handler
  const handleCartClick = useCallback(() => {
    router.push('/cart');
  }, [router]);

  const userLocale = locale === 'ar' ? 'ar' : 'en';

  return (
    <div className="header-inner hidden lg:block">
      <div className={`header-top-section ${locale === 'ar' ? 'flex' : 'flex flex-row-reverse'} justify-between items-center`}>
        <div className={`header-left ${locale === 'ar' ? 'flex' : 'flex flex-row-reverse'} basis-2/3 items-center`}>
          <div className="logo mr-6">
            <Link href="/">
              <Image 
                src={LogoImage} 
                alt="logo-img" 
                className="w-36"
                priority={false}
                loading="lazy"
              />
            </Link>
          </div>
          <form
            className={`search-bar relative w-full mr-12 ${locale === 'ar' ? 'flex' : 'flex flex-row-reverse'} items-center`}
            onSubmit={e => {
              e.preventDefault();
              SubmitSearch();
            }}
          >
            <div className="search-input w-full" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
              <input
                type="text"
                className="w-full border rounded-md border-gray-300 py-[0.8rem]"
                placeholder={`${tHeader('searchPlaceholder')}`}
                onChange={e => {
                  setSearch(e.target.value)
                }}
              />
            </div>
            <div className={`search-select px-4 absolute ${locale === 'ar' ? 'left-20' : 'right-6'}`}>
              <SearchDropdown category={category} setCategory={setCategory} />
            </div>
            <Button
              type="submit"
              bg="primary"
              classes={[
                `${locale === 'ar' ? 'left-0' : 'right-0'} text-white block absolute py-2 h-full px-8 rounded-l-md`,
              ]}
            >
              {`${tHeader('submit')}`}
            </Button>
          </form>
        </div>
        
        <div className={`header-right flex items-center ${locale === 'ar' ? 'mr-8' : 'ml-8'}`}>
          {!isLoading && user && (
            <button
              onClick={handleWishlistClick}
              className={`wishlist ${locale === 'ar' ? 'flex' : 'flex flex-row-reverse'}`}
              aria-label="Wishlist"
            >
              <WishlistIcon />
              <p className={`${locale === 'ar' ? 'mr-4' : 'ml-4'} text-primary font-semibold`}>
                {tHeader('wishlist')}
              </p>
            </button>
          )}
          
          <button
            data-testid="login-trigger"
            id="login-trigger"
            onClick={handleUserClick}
            className={`cursor-pointer user ${locale === 'ar' ? 'flex mr-12' : 'flex flex-row-reverse ml-2 gap-2'}`}
            aria-label="User account"
          >
            <UserIcon />
            <p className="mr-4 text-primary font-semibold">
              {!user ? tHeader('user') : `${tHeader('welcome')}, ${user?.first_name}`}
            </p>
          </button>
          
          <div className='mx-4'>
            <CurrencyDropdown />
          </div>
          
          <Suspense fallback={<div className="w-20" />}>
            <LazyLanguageSwitcher />
          </Suspense>
        </div>
      </div>
      
      <div className={`${locale === 'ar' ? 'flex' : 'flex flex-row-reverse'} header-bottom items-center justify-between pt-8`}>
        <div className={`header-right ${locale === 'ar' ? 'flex' : 'flex flex-row-reverse'} items-center`}>
          <Link
            href="/products"
            className={`${locale === 'ar' ? 'flex ml-6' : 'flex flex-row-reverse mr-6'} px-5 text-lg py-3 rounded-md items-center text-white bg-primary`}
          >
            <span className={locale === 'ar' ? 'ml-2' : 'mr-2'}>
              <GridIcon />
            </span>
            {tHeader('allCategories')}
            <span className={locale === 'ar' ? 'mr-2' : 'ml-2 rotate-180'}>
              <LeftArrowIcon />
            </span>
          </Link>
          
          <ul className="category-list-desktop">
            {featured_categories?.map((category: any) => (
              <CategoryItem key={category.id} category={category} locale={userLocale} />
            ))}
          </ul>
        </div>
        
        <div className="header-left">
          <button
            onClick={handleCartClick}
            className="cart-info bg-success rounded-md flex items-center py-[.5rem] px-6"
            aria-label={`Cart with ${cart.items.length} items, total ${symbol} ${cart.total}`}
          >
            <span className="ml-3">
              <CartIcon />
            </span>
            <div 
              key={currencyKey}
              className="cart-info-text pr-4 border-r-[1px] border-r-primary-500" 
              dir={locale === 'ar' ? 'rtl' : 'ltr'}
            >
              <p>{cart.items.length} {tHeader('items')}</p>
              <strong>{symbol} {cart.total}</strong>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
});

DesktopHeader.displayName = 'DesktopHeader';

export default DesktopHeader;